import { useCallback, useEffect, useRef, useState } from "react";
import { InteractionManager, Platform } from "react-native";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";
import { useSelector } from "react-redux";
import type { SharedValue } from "react-native-reanimated";
import {
  CoachOverlayService,
  CoachOverlayScreen,
} from "@services/CoachOverlayService";
import {
  useOverlayConfig,
  useOverlayConfigWeb,
} from "@redux/slices/globalSlice";
import { useTheme } from "styled-components/native";

interface UseCoachOverlayOptions {
  screen: CoachOverlayScreen;
  scrollRef?: React.RefObject<any>;
  autoStart?: boolean;
  delayMs?: number;
  enabled?: boolean;
  /**
   * yOffset SharedValue from useHideNavsOnScroll - used to check actual scroll position
   * This is more reliable than tracking via callbacks for reanimated scroll handlers
   */
  yOffset?: SharedValue<number>;
  /**
   * Whether this hook is rendered inside a NavigationContainer.
   * Set to false when used outside of navigation (e.g. inside a popover
   * portal) to avoid calling navigation hooks that would throw
   * "Couldn't find a navigation object".
   */
  hasNavigationContext?: boolean;
}

interface UseCoachOverlayReturn {
  start: () => void;
  stop: () => void;
  complete: () => void;
  isActive: boolean;
  isRunning: boolean;
  screenConfig: any;
  canStart: boolean;
  handleScroll: (event: any) => void;
}

export function useCoachOverlay(
  options: UseCoachOverlayOptions
): UseCoachOverlayReturn {
  const {
    screen,
    scrollRef,
    autoStart = true,
    delayMs = 300,
    enabled = true,
    yOffset,
    hasNavigationContext = true,
  } = options;

  const isWeb = Platform.OS === "web";
  const theme = useTheme() as { isLargeScreen?: boolean; isTablet?: boolean };
  const isResponsiveWeb = isWeb && (theme.isTablet || !theme.isLargeScreen);
  const effectiveEnabled = enabled && !isResponsiveWeb;

  // `hasNavigationContext` is invariant for the lifetime of the component, so
  // conditionally calling navigation hooks here keeps the hook order stable.

  const isFocused = hasNavigationContext ? useIsFocused() : false;
  const [isRunning, setIsRunning] = useState(false);
  const startTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollDelayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const interactionHandleRef = useRef<ReturnType<
    typeof InteractionManager.runAfterInteractions
  > | null>(null);
  const hasStartedRef = useRef(false);
  const isMountedRef = useRef(true);

  const overlayConfigMobile = useOverlayConfig();
  const overlayConfigWeb = useOverlayConfigWeb();
  const overlayConfig = isWeb ? overlayConfigWeb : overlayConfigMobile;
  const screenConfig = overlayConfig?.screens?.[screen] || {};

  const overlayActiveScreens = useSelector(
    (state: any) => state.appState?.overlayActiveScreens
  );
  const overlayActiveScreensWeb = useSelector(
    (state: any) => state.appState?.overlayActiveScreensWeb
  );

  const isActive = isWeb
    ? overlayActiveScreensWeb?.[screen] ?? false
    : overlayActiveScreens?.[screen] ?? false;

  const canStart =
    effectiveEnabled && isActive && screenConfig?.overlayActive === true;

  useEffect(() => {
    const unsubscribe = CoachOverlayService.subscribe((state) => {
      const running =
        state.activeTour === screen && state.tourState === "running";
      setIsRunning(running);

      if (
        state.tourState === "idle" &&
        hasStartedRef.current &&
        state.activeTour !== screen
      ) {
        hasStartedRef.current = false;
      }
    });

    return unsubscribe;
  }, [screen]);

  const clearPendingStart = useCallback(() => {
    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
    if (scrollDelayTimeoutRef.current) {
      clearTimeout(scrollDelayTimeoutRef.current);
      scrollDelayTimeoutRef.current = null;
    }
    if (interactionHandleRef.current) {
      interactionHandleRef.current.cancel();
      interactionHandleRef.current = null;
    }
    // Reset scroll-related state
    onScrollReachTopCallbackRef.current = null;
    scrollRetryCountRef.current = 0;
  }, []);

  // Track current scroll position (fallback when yOffset is not available)
  const currentScrollOffsetRef = useRef(0);

  // Helper to get current scroll offset - prefers yOffset.value (more reliable for reanimated)
  const getCurrentScrollOffset = useCallback((): number => {
    // Use yOffset.value if available (from reanimated SharedValue)
    if (yOffset !== undefined) {
      return yOffset.value;
    }
    // Fallback to manually tracked offset
    return currentScrollOffsetRef.current;
  }, [yOffset]);

  // Callback to update scroll position - screens should attach this to onScroll
  const handleScroll = useCallback(
    (event: any) => {
      const offset = event?.nativeEvent?.contentOffset?.y ?? 0;
      currentScrollOffsetRef.current = offset;

      // Check if scroll reached top and we have a pending callback
      // Use getCurrentScrollOffset for the most accurate reading
      const currentOffset = getCurrentScrollOffset();
      if (currentOffset <= 5 && onScrollReachTopCallbackRef.current) {
        const callback = onScrollReachTopCallbackRef.current;
        onScrollReachTopCallbackRef.current = null;
        scrollRetryCountRef.current = 0; // Reset retry count
        // Clear the timeout since scroll reached top
        if (scrollDelayTimeoutRef.current) {
          clearTimeout(scrollDelayTimeoutRef.current);
          scrollDelayTimeoutRef.current = null;
        }
        // Small delay to ensure layout is stable
        setTimeout(callback, 100);
      }
    },
    [getCurrentScrollOffset]
  );

  const scrollToTop = useCallback((): boolean => {
    if (scrollRef?.current) {
      try {
        // Use getCurrentScrollOffset for accurate position check
        const currentOffset = getCurrentScrollOffset();
        const needsScroll = currentOffset > 5;

        if (typeof scrollRef.current.scrollToOffset === "function") {
          scrollRef.current.scrollToOffset({ offset: 0, animated: true });
          return needsScroll;
        } else if (typeof scrollRef.current.scrollTo === "function") {
          scrollRef.current.scrollTo({ y: 0, animated: true });
          return needsScroll;
        }
      } catch {
        // Scroll may fail if component is unmounting
      }
    }
    return false;
  }, [scrollRef, getCurrentScrollOffset]);

  // Callback ref to call when scroll reaches top
  const onScrollReachTopCallbackRef = useRef<(() => void) | null>(null);
  // Track retry count to prevent infinite scroll attempts
  const scrollRetryCountRef = useRef(0);
  const MAX_SCROLL_RETRIES = 3;

  // Wait for scroll to reach top before calling callback
  // Verifies actual position and retries if user scrolled away
  const waitForScrollToTop = useCallback(
    (callback: () => void) => {
      onScrollReachTopCallbackRef.current = callback;

      const checkAndStart = () => {
        if (!isMountedRef.current || !isFocusedRef.current) {
          onScrollReachTopCallbackRef.current = null;
          scrollRetryCountRef.current = 0;
          return;
        }

        // Use getCurrentScrollOffset for accurate position (uses yOffset.value if available)
        const currentOffset = getCurrentScrollOffset();

        // If we're at the top (or close enough), start the overlay
        if (currentOffset <= 5) {
          if (onScrollReachTopCallbackRef.current) {
            const cb = onScrollReachTopCallbackRef.current;
            onScrollReachTopCallbackRef.current = null;
            scrollRetryCountRef.current = 0;
            // Small delay for layout stability
            setTimeout(cb, 100);
          }
        } else {
          // User scrolled away - check if we should retry
          scrollRetryCountRef.current += 1;

          if (scrollRetryCountRef.current < MAX_SCROLL_RETRIES) {
            // Re-scroll to top and wait again
            if (scrollRef?.current) {
              try {
                if (typeof scrollRef.current.scrollToOffset === "function") {
                  scrollRef.current.scrollToOffset({
                    offset: 0,
                    animated: true,
                  });
                } else if (typeof scrollRef.current.scrollTo === "function") {
                  scrollRef.current.scrollTo({ y: 0, animated: true });
                }
              } catch {
                // Ignore scroll errors
              }
            }
            // Wait for scroll animation and check again
            scrollDelayTimeoutRef.current = setTimeout(checkAndStart, 500);
          } else {
            // Max retries reached - cancel the overlay start
            onScrollReachTopCallbackRef.current = null;
            scrollRetryCountRef.current = 0;
          }
        }
      };

      // Initial wait for scroll animation
      scrollDelayTimeoutRef.current = setTimeout(checkAndStart, 500);
    },
    [scrollRef, getCurrentScrollOffset]
  );

  const isFocusedRef = useRef(isFocused);
  useEffect(() => {
    isFocusedRef.current = isFocused;
  }, [isFocused]);

  const start = useCallback(() => {
    if (!canStart || !isMountedRef.current || !isFocused) {
      return;
    }

    clearPendingStart();

    interactionHandleRef.current = InteractionManager.runAfterInteractions(
      () => {
        if (!isMountedRef.current || !isFocusedRef.current) {
          return;
        }

        startTimeoutRef.current = setTimeout(() => {
          if (!isMountedRef.current || !isFocusedRef.current) {
            return;
          }

          // Scroll to top just before starting the overlay
          const didScroll = scrollToTop();

          const startOverlay = () => {
            if (!isMountedRef.current || !isFocusedRef.current) {
              return;
            }

            const started = CoachOverlayService.requestStart({
              screen,
              scrollRef,
              isFocusedCheck: () =>
                isFocusedRef.current && isMountedRef.current,
              onComplete: () => {
                hasStartedRef.current = false;
              },
              onCancel: () => {
                hasStartedRef.current = false;
              },
            });

            if (started) {
              hasStartedRef.current = true;
            }
          };

          // If we needed to scroll, wait for scroll to complete before starting overlay
          if (didScroll) {
            // Clear any existing scroll delay timeout
            if (scrollDelayTimeoutRef.current) {
              clearTimeout(scrollDelayTimeoutRef.current);
            }
            // Use measure() to verify scroll is at top before starting overlay
            waitForScrollToTop(startOverlay);
          } else {
            startOverlay();
          }
        }, delayMs);
      }
    );
  }, [
    canStart,
    screen,
    delayMs,
    clearPendingStart,
    scrollToTop,
    waitForScrollToTop,
    isFocused,
  ]);

  const stop = useCallback(() => {
    clearPendingStart();
    if (CoachOverlayService.getActiveTour() === screen) {
      CoachOverlayService.cancelTour();
    }
    hasStartedRef.current = false;
  }, [screen, clearPendingStart]);

  const complete = useCallback(() => {
    clearPendingStart();
    if (CoachOverlayService.getActiveTour() === screen) {
      CoachOverlayService.completeTour();
    }
    hasStartedRef.current = false;
  }, [screen, clearPendingStart]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const prevIsActiveRef = useRef<boolean | null>(null);
  const pendingStartOnFocusRef = useRef(false);

  useEffect(() => {
    const isActiveChangedToTrue =
      prevIsActiveRef.current !== null && isActive && !prevIsActiveRef.current;

    if (isActiveChangedToTrue) {
      hasStartedRef.current = false;

      if (isFocused && canStart) {
        setTimeout(() => {
          if (isMountedRef.current && !hasStartedRef.current) {
            start();
          }
        }, 100);
      } else {
        // Set pending start even if canStart is false (e.g., when screen is not focused)
        // This allows the overlay to start when the screen becomes focused
        pendingStartOnFocusRef.current = true;
      }
    }
    prevIsActiveRef.current = isActive;
  }, [isActive, screen, isFocused, canStart, start]);

  const prevIsFocusedRef = useRef(isFocused);
  const prevCanStartRef = useRef(canStart);

  useEffect(() => {
    const justBecameFocused = isFocused && !prevIsFocusedRef.current;
    const canStartJustBecameTrue = canStart && !prevCanStartRef.current;

    // Start if we just became focused with pending start and can start
    // OR if canStart just became true while focused with pending start
    if (
      pendingStartOnFocusRef.current &&
      canStart &&
      isFocused &&
      !hasStartedRef.current &&
      (justBecameFocused || canStartJustBecameTrue)
    ) {
      pendingStartOnFocusRef.current = false;
      setTimeout(() => {
        if (isMountedRef.current && !hasStartedRef.current) {
          start();
        }
      }, 100);
    }

    prevIsFocusedRef.current = isFocused;
    prevCanStartRef.current = canStart;
  }, [isFocused, canStart, screen, start]);

  useEffect(() => {
    if (!isFocused) {
      clearPendingStart();
      if (CoachOverlayService.getActiveTour() === screen) {
        CoachOverlayService.cancelTour();
      }
      hasStartedRef.current = false;
    }
  }, [isFocused, screen, clearPendingStart]);

  const shouldAutoStartRef = useRef(false);

  const focusEffectCallback = useCallback(() => {
    if (autoStart) {
      if (canStart && !hasStartedRef.current) {
        start();
      } else if (!canStart) {
        shouldAutoStartRef.current = true;
      }
    }

    return () => {
      clearPendingStart();
      shouldAutoStartRef.current = false;
      if (CoachOverlayService.getActiveTour() === screen) {
        CoachOverlayService.cancelTour();
      }
      hasStartedRef.current = false;
    };
  }, [autoStart, canStart, screen, start, clearPendingStart]);

  // `hasNavigationContext` is invariant for the lifetime of the component, so
  // conditionally calling this navigation hook keeps the hook order stable.
  if (hasNavigationContext) {
    useFocusEffect(focusEffectCallback);
  }

  useEffect(() => {
    if (
      isFocused &&
      autoStart &&
      canStart &&
      !hasStartedRef.current &&
      shouldAutoStartRef.current
    ) {
      shouldAutoStartRef.current = false;
      start();
    }
  }, [isFocused, autoStart, canStart, start]);

  useEffect(() => {
    return () => {
      clearPendingStart();
      if (CoachOverlayService.getActiveTour() === screen) {
        CoachOverlayService.cancelTour();
      }
    };
  }, [screen, clearPendingStart]);

  return {
    start,
    stop,
    complete,
    isActive,
    isRunning,
    screenConfig,
    canStart,
    handleScroll,
  };
}

export default useCoachOverlay;
